import { useRef, type ChangeEvent, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { brandAssets } from "../assets/brand";
import { tokens } from "../theme";

interface AttachButtonProps {
  onAttach?: (files: File[]) => void;
  disabled?: boolean;
  /** Max reached — button stays visible but inert. */
  full?: boolean;
  style?: CSSProperties;
}

/** Supported upload formats for now — JPEG and PNG only. */
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png"];
const ACCEPTED_EXTENSIONS = /\.(jpe?g|png)$/i;

// Some pickers report an empty MIME type; fall back to the file extension.
function isAcceptedImage(file: File): boolean {
  if (file.type) return ACCEPTED_IMAGE_TYPES.includes(file.type);
  return ACCEPTED_EXTENSIONS.test(file.name);
}

const styles: Record<string, CSSProperties> = {
  button: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: "none",
    background: tokens.icon.sparklesBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    flexShrink: 0,
  },
  // Tinted via mask so the glyph follows the config accent colour.
  // Quoted: Vite inlines small SVGs as data: URIs that break a bare url().
  icon: {
    width: 20,
    height: 20,
    backgroundColor: tokens.icon.accent,
    maskImage: `url("${brandAssets.attachment}")`,
    WebkitMaskImage: `url("${brandAssets.attachment}")`,
    maskRepeat: "no-repeat",
    WebkitMaskRepeat: "no-repeat",
    maskPosition: "center",
    WebkitMaskPosition: "center",
    maskSize: "contain",
    WebkitMaskSize: "contain",
  },
};

/**
 * JPEG/PNG picker. iOS converts HEIC photos to JPEG because HEIC isn't listed
 * in `accept`. No `capture` attribute on purpose — it forces camera-only;
 * without it iOS/Android offer both camera and gallery.
 */
export default function AttachButton({
  onAttach,
  disabled,
  full,
  style,
}: AttachButtonProps) {
  const inert = disabled || full;
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    // `accept` is only a picker hint — "All files" can bypass it.
    const files = Array.from(event.target.files ?? []).filter(isAcceptedImage);
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
    if (files.length > 0) onAttach?.(files);
  };

  return (
    <>
      <button
        type="button"
        style={{
          ...styles.button,
          opacity: inert ? 0.6 : 1,
          cursor: inert ? "default" : "pointer",
          ...style,
        }}
        onClick={() => fileInputRef.current?.click()}
        disabled={inert}
        aria-label={t("input.attach")}
      >
        <span style={styles.icon} aria-hidden="true" />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES.join(",")}
        multiple
        hidden
        onChange={handleFileChange}
      />
    </>
  );
}
